import { escapeHtml } from "../acrotohtml/escapeHtml";
import type { DocXItem } from "./types";

export function renderDocXItem(item: DocXItem): string {
  const id = escapeHtml(item.itemId);
  const elementId = escapeHtml(item.Id)?.toLowerCase();
  const value = item.Value ? escapeHtml(item.Value) : "";
  const placeholder = item.Placeholder ? escapeHtml(item.Placeholder) : "";

  // If the item has a formula, render it as a readonly calculated field
  if (item.Formula) {
    const formulaData = escapeHtml(item.Formula)?.toLowerCase();
    return `
      <input 
        type="text" 
        data-id="${id}" 
        data-element-id="${elementId}"
        data-formula="${formulaData}"
        data-format="${item.Format ? (item.Format) : ''}"
        class="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-gray-50 focus:outline-none focus:border-blue-500 focus:ring-3 focus:ring-blue-500/10 transition-all" 
        placeholder="${placeholder}"
        value="${value}"
        readonly
      />
    `;
  }

  switch (item.itemType) {
    case "Label": {
      return `<span class="label text-sm font-medium text-gray-700">${value}</span>`;
    }
    case "Text":
    case "InlineText": {
      return `
        <input 
          type="text" 
          data-id="${id}" 
          data-element-id="${elementId}"
          data-format="${item.Format ? (item.Format) : ''}"
          data-textbox="true"
          data-item-type="${item.itemType}"
          class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500 focus:ring-3 focus:ring-blue-500/10 transition-all" 
          placeholder="${placeholder}"
          value="${value}"
        />
      `;
    }
    case "TextRow": {
      const rowCount =
        typeof item.RowCount === "number" && item.RowCount > 0
          ? item.RowCount
          : 0;

      const safePlaceholder = placeholder || "";

      const inputsHtml = Array.from({ length: rowCount })
        .map((_, index) => {
          return `
        <input
          type="text"
          data-index="${index}"
          style="width:100vh"
          class="w-full px-3 py-2 border border-gray-300 rounded text-sm
                 focus:outline-none focus:border-blue-500
                 focus:ring-3 focus:ring-blue-500/10 transition-all
                 "
          placeholder="${safePlaceholder}"
        />
      `;
        })
        .join("");

      return `
    <div
      data-id="${id}" data-type="TextRow"
      class="flex flex-col gap-2"
    >
      ${inputsHtml}
    </div>
  `;
    }

    case "Date": {
      return `
        <input 
          type="date" 
          data-id="${id}" 
          data-element-id="${elementId}"
          data-format="${item.Format ? (item.Format) : ''}"
          class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500 focus:ring-3 focus:ring-blue-500/10 transition-all" 
          placeholder="${placeholder}"
          value="${value}"
        />
      `;
    }
    case "Checkbox": {
      const isChecked = value.includes("checked") ? "checked" : "";
      return `
        <label class="flex items-center cursor-pointer !mt-0 !mb-0">
          <input 
            type="checkbox" 
            data-id="${id}" 
            data-element-id="${elementId}"
            data-format="${item.Format ? (item.Format) : ''}"
            class="w-4 h-4 cursor-pointer !mr-0" 
            ${isChecked}
          />
          <span class="text-sm text-gray-700">${value}</span>
        </label>
      `;
    }
    case "Dropdown": {
      const options = item.Options || [];
      const optionsHtml = options
        .map((opt) => {
          const optValue = escapeHtml(opt.value);
          const optLabel = escapeHtml(opt.label);
          const isSelected = opt.checked ? "selected" : "";
          return `<option value="${optValue}" ${isSelected}>${optLabel}</option>`;
        })
        .join("");
      return `
        <select data-id="${id}" data-element-id="${elementId}" class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500 focus:ring-3 focus:ring-blue-500/10 transition-all">
          <option value="">${placeholder}</option>
          ${optionsHtml}
        </select>
      `;
    }
    case "Radio": {
      const radioOptions = item.Options || [];
      const radioHtml = radioOptions
        .map((opt) => {
          const optValue = escapeHtml(opt.value);
          const optLabel = escapeHtml(opt.label);
          const isRadioChecked = opt.checked ? "checked" : "";
          return `
            <label class="flex items-center cursor-pointer !mt-0 !mb-0">
              <input 
                type="radio" 
                name="${id}" 
                value="${optValue}" 
                data-id="${id}-${optValue}" 
                data-element-id="${elementId}"
                data-format="${item.Format ? (item.Format) : ''}"
                class="w-4 h-4 cursor-pointer !mr-0" 
                ${isRadioChecked}
              />
              <span class="text-sm text-gray-700">${optLabel}</span>
            </label>
          `;
        })
        .join("");
      return radioHtml;
    }
    default: {
      return "";
    }
  }
}
